import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Card,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Edit,
  Delete,
} from "@mui/icons-material";
import { getHomeLinksByCountry } from "../../redux/actions/GetHomeLinksByCountryAction";
import { DeleteHomeLink } from "../../redux/actions/DeleteHomeLinkAction";
import AddHomeLinkModal from "components/AddHomeLinkModal";
import EditHomeLinkModal from "components/EditHomeLinkModal";

const selectedCountry = localStorage.getItem("selectedCountry");

const HomeLinksTable = () => {
  const dispatch = useDispatch();
  const homeLinksResponse = useSelector((state) => state.homeLinks.homeLinks);
  const fetchStatus = useSelector((state) => state.homeLinks.status);
  const deleteStatus = useSelector((state) => state.deleteHomeLink.status);
  const [homeLinks, setHomeLinks] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedHomeLink, setSelectedHomeLink] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (homeLinksResponse) {
      const listHomeLinks = homeLinksResponse.map((element) => ({
        id: element.idhomeLink,
        idhomeLink: element.idhomeLink,
        name: element.name,
        url: element.url,
        imagePath: element.imagePath,
        countryId: element.country?.idcountry,
        countryName: selectedCountry ?? "",
      }));

      setHomeLinks(listHomeLinks);
    }
  }, [homeLinksResponse]);

  useEffect(() => {
    dispatch(getHomeLinksByCountry());
  }, [dispatch]);

  const handleEditOpen = (row) => {
    setSelectedHomeLink(row);
    setOpenEditModal(true);
  };

  const handleDeleteOpen = (row) => {
    setSelectedHomeLink(row);
    setDeleteMessage({ type: "", text: "" });
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    setSelectedHomeLink(null);
    setDeleteMessage({ type: "", text: "" });
  };

  const handleDeleteConfirm = async () => {
    setDeleteMessage({ type: "", text: "" });

    try {
      const result = await dispatch(
        DeleteHomeLink(selectedHomeLink.idhomeLink)
      );

      if (result.success) {
        setDeleteMessage({ type: "success", text: result.message });
        setTimeout(() => {
          handleDeleteClose();
        }, 1500);
      } else {
        setDeleteMessage({ type: "error", text: result.message });
      }
    } catch {
      setDeleteMessage({
        type: "error",
        text: "There was an error deleting the home link. Please try again.",
      });
    }
  };

  const columns = [
    {
      field: "idhomeLink",
      headerName: "ID",
      width: 80,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "url",
      headerName: "URL",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </span>
          <IconButton
            size="small"
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "imagePath",
      headerName: "Image",
      width: 120,
      renderCell: (params) =>
        params.row.url?.toLowerCase().includes("youtube.com") ? (
          <p></p>
        ) : (
          <Box
            component="img"
            src={params.value}
            alt={params.row.name}
            sx={{ width: 80, height: 45, objectFit: "cover", borderRadius: 1 }}
          />
        ),
    },
    {
      field: "countryName",
      headerName: "Country",
      width: 140,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            sx={{
              color: "#ffffff",
              backgroundColor: "#4b4bfa",
              minWidth: 40,
              "&:hover": { backgroundColor: "#3e3ed9" },
            }}
            onClick={() => handleEditOpen(params.row)}
          >
            <Edit />
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ minWidth: 40 }}
            onClick={() => handleDeleteOpen(params.row)}
          >
            <Delete />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <div style={{ height: "100%", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <h1 style={{ marginBottom: "0.5%" }}>Home Links Management</h1>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#4b4bfa",
                  color: "#ffffff",
                  "&:hover": { backgroundColor: "#3e3ed9" },
                }}
                onClick={() => setOpenAddModal(true)}
              >
                <AddIcon />
              </Button>
            </Box>
            <DataGrid
              rows={homeLinks}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              loading={fetchStatus === "loading"}
              sx={{
                "& .MuiCircularProgress-root": { color: "#4b4bfa" },
                mt: 2,
              }}
            />
          </div>
        </Card>
      </Grid>

      <AddHomeLinkModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
      />
      <EditHomeLinkModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedHomeLink(null);
        }}
        homeLinkData={selectedHomeLink}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Home Link</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            Are you sure you want to delete{" "}
            <strong>{selectedHomeLink?.name}</strong>?
          </Box>
          {deleteMessage.text && (
            <Alert severity={deleteMessage.type} sx={{ mt: 2 }}>
              {deleteMessage.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleDeleteClose}
            disabled={deleteStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteStatus === "loading"}
            startIcon={
              deleteStatus === "loading" ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deleteStatus === "loading" ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default HomeLinksTable;
